import {BaseBadge} from "@/components/BaseBadge";
import {PATHS} from "@/constants/routes";
import type {DashboardRecentCampaign} from "@/types/push.types";
import {formatDistanceToNow} from "@/utils/date.utils";
import {ArrowRight} from "lucide-react";
import {useNavigate} from "react-router";

interface RecentCampaignsProps {
  campaigns: DashboardRecentCampaign[];
}

export const RecentCampaigns = ({campaigns}: RecentCampaignsProps) => {
  const navigate = useNavigate();

  const formatDate = (iso: string) => {
    try {
      return formatDistanceToNow(new Date(iso));
    } catch {
      return "—";
    }
  };

  if (campaigns.length === 0) {
    return (
      <div className="text-center py-8">
        <p
          className="text-sm"
          style={{color: "var(--color-text-secondary)"}}
        >
          No campaigns yet
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr style={{borderBottom: "1px solid var(--color-surface-2)"}}>
            <th
              className="text-left py-2 px-3 text-xs font-semibold uppercase tracking-wide"
              style={{color: "var(--color-text-secondary)"}}
            >
              Title
            </th>
            <th
              className="text-left py-2 px-3 text-xs font-semibold uppercase tracking-wide"
              style={{color: "var(--color-text-secondary)"}}
            >
              Status
            </th>
            <th
              className="text-right py-2 px-3 text-xs font-semibold uppercase tracking-wide"
              style={{color: "var(--color-text-secondary)"}}
            >
              Recipients
            </th>
            <th
              className="text-right py-2 px-3 text-xs font-semibold uppercase tracking-wide"
              style={{color: "var(--color-text-secondary)"}}
            >
              Read Rate
            </th>
            <th
              className="text-left py-2 px-3 text-xs font-semibold uppercase tracking-wide"
              style={{color: "var(--color-text-secondary)"}}
            >
              Sent
            </th>
            <th className="w-12" />
          </tr>
        </thead>
        <tbody>
          {campaigns.map(campaign => (
            <tr
              key={campaign.id}
              className="group cursor-pointer transition-colors"
              style={{borderBottom: "1px solid var(--color-surface-2)"}}
              onClick={() => navigate(PATHS.PUSH_CAMPAIGN_DETAIL(campaign.id))}
              onMouseEnter={e => {
                e.currentTarget.style.backgroundColor = "var(--color-surface-2)";
              }}
              onMouseLeave={e => {
                e.currentTarget.style.backgroundColor = "transparent";
              }}
            >
              <td className="py-3 px-3">
                <p
                  className="text-sm font-medium"
                  style={{color: "var(--color-text)"}}
                >
                  {campaign.title}
                </p>
              </td>
              <td className="py-3 px-3">
                <BaseBadge
                  label={campaign.status}
                  variant={["sent", "scheduled"].includes(campaign.status) ? "success" : "neutral"}
                />
              </td>
              <td className="py-3 px-3 text-right">
                <span
                  className="text-sm"
                  style={{color: "var(--color-text)"}}
                >
                  {campaign.totalRecipients.toLocaleString()}
                </span>
              </td>
              <td className="py-3 px-3 text-right">
                <span
                  className="text-sm font-semibold"
                  style={{color: campaign.readRate > 50 ? "var(--color-success)" : "var(--color-text)"}}
                >
                  {campaign.readRate.toFixed(1)}%
                </span>
              </td>
              <td className="py-3 px-3">
                <span
                  className="text-sm"
                  style={{color: "var(--color-text-secondary)"}}
                >
                  {campaign.sentAt ? formatDate(campaign.sentAt) : "—"}
                </span>
              </td>
              <td className="py-3 px-3">
                <ArrowRight
                  size={16}
                  className="opacity-0 group-hover:opacity-100 transition-opacity"
                  style={{color: "var(--color-text-secondary)"}}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
